import {Person, PersonFilterOptions, PersonListItem, PersonPayIndicatorListItem, PersonEmploymentStatusListItem, UpdatePerson} from "@/services/models/Person";
import {PagedData} from "@/services/models/shared/PagedData";
import {AxiosResponse} from "axios";
import FileProvider from "@/utilities/providers/FileProvider";
import {Role, PersonDelegate, NewPersonDelegate, DeletePersonDelegate, RoleFilterOptions} from "@/services/models/Role";
import BimsHttpService from "@/services/BimsHttpService";

class PersonService {
    /**
     *  @description Retrieves all people
     */
    getAllPeople(onlyShowTTI: boolean | undefined): Promise<PersonListItem[]> {
        const vm = this;
        return vm.getPeopleList({
            onlyShowActive: true,
            onlyShowTTI: onlyShowTTI ?? true,
            employeeClassification: null,
            pageSize: 1,
        } as PersonFilterOptions)
            .then((response) => {
                return vm.getPeopleList({
                    onlyShowActive: true,
                    onlyShowTTI: onlyShowTTI ?? true,
                    employeeClassification: null,
                    pageSize: response.totalCount,
                    page: 1
                } as PersonFilterOptions)
                    .then((responses) => {
                        return responses.data;
                    })
            })
    }

    /**
     * @description Returns a paged list of people.
     */
    getPeopleList(filter: PersonFilterOptions): Promise<PagedData<PersonListItem>> {
        return BimsHttpService.post(`/people`, filter).then((response: AxiosResponse<PagedData<PersonListItem>>) => {
            return response.data as PagedData<PersonListItem>;
        });
    }

    getPayIndicatorList(): Promise<Array<PersonPayIndicatorListItem>> {
        return BimsHttpService.get(`/people/payindicators`).then((response: AxiosResponse<Array<PersonPayIndicatorListItem>>) => {
            return response.data as Array<PersonPayIndicatorListItem>;
        });
    }

    getEmploymentStatuses(): Promise<Array<PersonEmploymentStatusListItem>> {
        return BimsHttpService.get(`/people/employmentStatuses`).then((response: AxiosResponse<Array<PersonEmploymentStatusListItem>>) => {
            return response.data as Array<PersonEmploymentStatusListItem>;
        });
    }

    getEmployeeTypes(): Promise<Array<string>> {
        return BimsHttpService.get(`/people/employeeTypes`).then((response: AxiosResponse<Array<string>>) => {
            return response.data as Array<string>;
        });
    }

    getExternalOrganizations(): Promise<Array<string>> {
        return BimsHttpService.get(`/people/externalorganizations`).then((response: AxiosResponse<Array<string>>) => {
            return response.data as Array<string>;
        });
    }

    /**
     * @description Returns the person with the specified id
     */
    getPerson(id: string): Promise<Person> {
        return BimsHttpService.get(`/people/${id}`).then((response: AxiosResponse<Person>) => {
            return response.data as Person;
        });
    }

    /**
     *  @description Updates the general information for an organization
     */
    updatePerson(person: UpdatePerson): Promise<any> {
        return BimsHttpService.put("/people/" + person.id,
            person
        )
    }

    /**
     * @description Downloads the CSV export for the people listing
     */
    public getExcelExport(
        filter: PersonFilterOptions
    ): Promise<any> {
        return BimsHttpService.get(
            `people/export`, {
                params: filter
            }).then(response => {
            return FileProvider.ForceFileDownload(response)
        })
    }

    /**
     * @description Downloads the CSV export for the people listing
     */
    public getNewExcelExport(
        filter: PersonFilterOptions
    ): Promise<any> {
        return BimsHttpService.post(
            `people/export`, filter).then(response => {
            return FileProvider.ForceFileDownload(response)
        })
    }

    /**
     *  @description Retrieves all application roles
     */
    getAllRoles(filter: RoleFilterOptions): Promise<Role[]> {
        return BimsHttpService.get(`/roles`, {
            params: filter
        }).then((response: AxiosResponse<Role[]>) => {
            return response.data as Role[];
        });
    }

    /**
     *  @description Retrieves all application roles for a person
     */
    getRolesForPerson(personId: string, filter: RoleFilterOptions): Promise<Role[]> {
        return BimsHttpService.get(`people/${personId}/roles`, {
            params: filter
        }).then((response: AxiosResponse<Role[]>) => {
            return response.data as Role[];
        });
    }

    /**
     *  @description Retrieves all delegated application roles and the delegate for a person
     */
    getPersonDelegates(personId: string): Promise<PersonDelegate[]> {
        return BimsHttpService.get(`people/${personId}/roles/delegates`).then((response: AxiosResponse<PersonDelegate[]>) => {
            return response.data as PersonDelegate[];
        });
    }

    /**
     *  @description Adds new person delegate
     */
    addPersonDelegate(personId: string, delegate: NewPersonDelegate): Promise<string> {
        return BimsHttpService.post(`people/${personId}/roles/delegates`,
            delegate
        ).then(response => {
            return response.data
        });
      }
      

    /**
     *  @description Deletes existing person delegate
     */
    deletePersonDelegate(personId: string, delegate: DeletePersonDelegate): Promise<string> {
        return BimsHttpService.post(`people/${personId}/roles/delegates/remove`,
            delegate
        ).then(response => {
            return response.data
        });
      }

    /**
     *  @description Sets the application roles for th person
     */
    updateRolesForPerson(personId: string, roles: Role[]): Promise<any> {
        return BimsHttpService.put(`people/${personId}/roles`, roles);
    }
}

export default new PersonService()
